import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMemo } from "react";

import { isVideoMimetype } from "@/utils/mimetype";

import { SelectedObject } from "../../selection-sidebar/types";

const MAX_STACKED_IMAGES = 3;

const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  border: `1px solid ${theme.palette.secondary[5]}`,
  overflow: "hidden",
}));

const ImageStack = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  padding: "48px",
});

const StyledPreviewImage = styled("img")({
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  borderRadius: 4,
});

const StackedImageWrapper = styled(Box)({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "90%",
  height: "90%",
});

interface ExportPreviewProps {
  objects: SelectedObject[];
}

const getImageUrl = (obj: SelectedObject): string | undefined => {
  if (obj.__typename === "Asset") {
    return obj.previewUrl ?? undefined;
  }
  if (obj.__typename === "File") {
    if (isVideoMimetype(obj.contentType)) {
      return obj.previewUrl ?? obj.thumbnails?.large?.url;
    }
    return obj.url ?? obj.previewUrl ?? obj.thumbnails?.large?.url;
  }
  return undefined;
};

export const ExportPreview = (props: ExportPreviewProps) => {
  const { objects } = props;

  const imagePreviews = useMemo(
    () =>
      (objects?.slice(0, MAX_STACKED_IMAGES) ?? [])
        .map((obj) => ({
          id: obj.id,
          name: obj.name ?? "Image",
          imageUrl: getImageUrl(obj),
        }))
        .filter((preview) => preview.imageUrl),
    [objects],
  );

  if (imagePreviews.length === 0) {
    return <PreviewContainer />;
  }

  // Single image - no stacking
  if (imagePreviews.length === 1) {
    return (
      <PreviewContainer>
        <StyledPreviewImage
          src={imagePreviews[0].imageUrl}
          alt={imagePreviews[0].name}
        />
      </PreviewContainer>
    );
  }

  const totalImages = imagePreviews.length;

  return (
    <PreviewContainer>
      <ImageStack>
        {imagePreviews.toReversed().map((preview, index) => {
          const rotation = (totalImages - 1 - index) * 10;
          return (
            <StackedImageWrapper
              key={preview.id}
              sx={{
                transform: rotation ? `rotate(${rotation}deg)` : "none",
                zIndex: index + 1,
              }}
            >
              <StyledPreviewImage src={preview.imageUrl} alt={preview.name} />
            </StackedImageWrapper>
          );
        })}
      </ImageStack>
    </PreviewContainer>
  );
};
